import { useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SEOHead = ({ title, description }) => {
  const [seo, setSeo] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/seo`).then(res => setSeo(res.data)).catch(() => {});
  }, []);

  // Compute all SEO values
  const seoData = useMemo(() => {
    if (!seo) return null;
    const siteTitle = seo.site_title || 'SENDBOX';
    const pageTitle = title ? `${title} | ${siteTitle}` : siteTitle;
    const pageDesc = description || seo.meta_description || '';
    const robots = `${seo.robots_index !== false ? 'index' : 'noindex'}, ${seo.robots_follow !== false ? 'follow' : 'nofollow'}`;
    return { siteTitle, pageTitle, pageDesc, robots };
  }, [seo, title, description]);

  // Set document.title (no portal needed for this)
  useEffect(() => {
    if (seoData) document.title = seoData.pageTitle;
  }, [seoData]);

  // Google Analytics (imperative — scripts must execute)
  useEffect(() => {
    if (!seo?.google_analytics_id) return;
    if (document.querySelector('script[data-seo="ga"]')) return;

    const gaScript = document.createElement('script');
    gaScript.async = true;
    gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${seo.google_analytics_id}`;
    gaScript.setAttribute('data-seo', 'ga');
    document.head.appendChild(gaScript);

    const gaInit = document.createElement('script');
    gaInit.setAttribute('data-seo', 'ga-init');
    gaInit.textContent = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${seo.google_analytics_id}');`;
    document.head.appendChild(gaInit);

    return () => {
      document.querySelector('script[data-seo="ga"]')?.remove();
      document.querySelector('script[data-seo="ga-init"]')?.remove();
    };
  }, [seo?.google_analytics_id]);

  if (!seo || !seoData) return null;

  const { pageTitle, pageDesc, robots } = seoData;

  const structuredData = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: seo.structured_data_name || seoData.siteTitle,
    description: seo.structured_data_description || pageDesc,
    url: seo.canonical_url || BACKEND_URL,
    applicationCategory: "FileTransfer",
    operatingSystem: "Web"
  });

  // Render meta/link tags declaratively via portal into <head>
  return createPortal(
    <>
      {/* Basic meta */}
      <meta name="description" content={pageDesc} data-seo="managed" />
      {seo.meta_keywords && <meta name="keywords" content={seo.meta_keywords} data-seo="managed" />}
      <meta name="robots" content={robots} data-seo="managed" />

      {/* Open Graph */}
      <meta property="og:title" content={seo.og_title || pageTitle} data-seo="managed" />
      <meta property="og:description" content={seo.og_description || pageDesc} data-seo="managed" />
      <meta property="og:type" content="website" data-seo="managed" />
      {seo.og_image && <meta property="og:image" content={seo.og_image} data-seo="managed" />}
      {seo.canonical_url && <meta property="og:url" content={seo.canonical_url} data-seo="managed" />}

      {/* Twitter */}
      {seo.twitter_card && <meta name="twitter:card" content={seo.twitter_card} data-seo="managed" />}
      {seo.twitter_site && <meta name="twitter:site" content={seo.twitter_site} data-seo="managed" />}
      <meta name="twitter:title" content={seo.og_title || pageTitle} data-seo="managed" />
      {(seo.og_description || pageDesc) && <meta name="twitter:description" content={seo.og_description || pageDesc} data-seo="managed" />}
      {seo.og_image && <meta name="twitter:image" content={seo.og_image} data-seo="managed" />}

      {/* Canonical */}
      {seo.canonical_url && <link rel="canonical" href={seo.canonical_url} data-seo="managed" />}

      {/* Favicon */}
      {seo.favicon_url && <link rel="icon" href={seo.favicon_url} data-seo="managed" />}

      {/* Structured Data */}
      <script type="application/ld+json" data-seo="structured" dangerouslySetInnerHTML={{ __html: structuredData }} />
    </>,
    document.head
  );
};

export default SEOHead;
